import React, { useEffect, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  useWindowDimensions,
  SafeAreaView,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  LayoutChangeEvent,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import { Column, Row, Grid, Container } from "ui-grid";
import {
  Button as ButtonComponent,
  ButtonType,
  Icons,
  SimpleAccordion,
  theme as componentTheme,
  Text as TextComponent,
  ErrorWarningBottomSheet,
} from "ui-components";
import { fontWeights, typography } from "ui-components/dist/theme/typography";
import { Screen, AddPaymentCCDebit, AddPaymentBA } from "../../components";
import { MoreScreenProps } from "../../navigation/more-stack/more-stack";
import { usePaymentMethodInformation } from "../../hooks/usePaymentMethodInformation";
import { PaymentSourceType } from "../../enums";
import { useReservationInformation } from "../../hooks/useReservationInformation";
import { PaymentMethod } from "../../components/new/wallet-payment-methods/payment-method";
import { RootState } from "../../redux/reducers";
import { DomusoApiService } from "../../api/DomusoApiService";
import { translate } from "../../i18n";
import automationTestingLocators from "./utils/automation-testing-locators.json";
import { FullScreenLoader } from "../../components/new/loader/full-screen-loader";
import { Footer } from "../../components/new/footer/footer";

const { CREDIT_CARD, BANK_ACCOUNT } = PaymentSourceType;

interface OwnProps extends MoreScreenProps {}

export type Props = OwnProps;

export const WalletPaymentMethods = ({ route }: Props) => {
  const { width: screenWidth } = useWindowDimensions();
  const [updated, setUpdated] = useState(false);

  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const service = new DomusoApiService(cognitoUser, dispatch);
  const styles = getStyles();
  const { firstReservationId } = useReservationInformation();

  const {
    paymentSources,
    isLoading,
    error: paymentSourcesError,
  } = usePaymentMethodInformation({
    keepInactive: true,
    updated: updated,
    allowACH: true,
    allowCC: true,
  });

  let [expandCreditCard, setExpandCreditCard] = useState(false);
  let [expandBankAccount, setExpandBankAccount] = useState(false);
  let [toggleForBankBottomSheet, setToggleForBankBottomSheet] = useState(false);
  let [toggleForCardBottomSheet, setToggleForCardBottomSheet] = useState(false);
  let [cardLoading, setCardLoading] = useState(false);
  let [bankLoading, setBankLoading] = useState(false);
  let [errorMsg, setErrorMsg] = useState("");
  let [bankPosition, setBankPosition] = useState(0);

  const refCCDebit = useRef();
  const refBankAccount = useRef();
  const refPopupWarning = useRef();
  const bankViewRef = useRef<View>(null);
  const scrollViewRef = useRef<ScrollView>(null);

  const CommonButton = ({ tx, text, onPress, testProp }: any) => (
    <ButtonComponent
      tx={tx}
      text={text}
      mode="contained"
      onPress={onPress}
      type={ButtonType.secondary}
      leftIcon={Icons.add}
      showLeftIcon={true}
      borderColor={componentTheme.colors.mediumGrey}
      textStyle={{
        fontFamily: "Lato_400Regular",
        fontWeight: fontWeights.Regular,
        fontSize: componentTheme.fontSize.title2,
      }}
      style={{
        width: "70%",
        paddingVertical: 8,
        maxWidth: "70%",
      }}
      {...testProp}
    />
  );

  const creditCardPaymentMethod = (paymentSources: any) =>
    (Array.isArray(paymentSources) ? paymentSources : []).filter(
      (payment) => payment.type === CREDIT_CARD
    );

  const bankAccountPaymentMethod = (paymentSources: any) =>
    (Array.isArray(paymentSources) ? paymentSources : []).filter(
      (payment) => payment.type === BANK_ACCOUNT
    );

  useEffect(() => {
    if (
      paymentSources &&
      (creditCardPaymentMethod(paymentSources)?.length == 1 ||
        creditCardPaymentMethod(paymentSources)?.length === 0)
    ) {
      setExpandCreditCard(true);
    }

    if (
      paymentSources &&
      (bankAccountPaymentMethod(paymentSources)?.length == 1 ||
        bankAccountPaymentMethod(paymentSources)?.length === 0)
    ) {
      setExpandBankAccount(true);
    }
  }, [paymentSources]);

  const updatePaymentMethodNickname = async (id: string | number, nickname: string = "") => {
    try {
      const body = {
        payment_source_id: id,
        nickname: nickname && nickname?.length > 0 ? nickname : null,
      };
      let resp = await service.updatePaymentSource(body);
      setUpdated(!updated);
    } catch (error: any) {
      if (error?.message == "Failed to fetch" || error?.message == "Network request failed") {
        setErrorMsg(translate("walletPaymentMethod.connectionErrorMessage") as string);
      } else {
        setErrorMsg(translate("walletPaymentMethod.errorUpdatingNickname") as string);
      }
      refPopupWarning?.current?.open();
    }
  };

  const scrollToPosition = (position?: number) => {
    if (Platform.OS === "web") {
      window?.scroll({ top: position });
    } else {
      scrollViewRef?.current?.scrollTo({ y: position });
    }
  };

  const scrollTo = (scrollTo?: string) => {
    if (scrollTo === "bank") {
      if (Platform.OS === "web") {
        bankViewRef.current?.measure((x, y) => {
          scrollToPosition(y);
        });
      } else {
        scrollToPosition(bankPosition);
      }
    } else {
      scrollToPosition(0);
    }
  };
  const onLayout = ({
    nativeEvent: {
      layout: { y },
    },
  }: LayoutChangeEvent) => {
    setBankPosition(y);
  };

  useEffect(() => {
    if (paymentSourcesError) {
      if (
        paymentSourcesError?.message == "Failed to fetch" ||
        paymentSourcesError?.message == "Network request failed"
      ) {
        setErrorMsg(translate("walletPaymentMethod.connectionErrorMessage") as string);
      } else {
        setErrorMsg(translate("walletPaymentMethod.unexpectedError") as string);
      }

      refPopupWarning?.current?.open();
    }
  }, [paymentSourcesError]);

  useEffect(() => {
    if (paymentSources && paymentSources?.length >= 0) {
      if (bankLoading) {
        if (Platform.OS === "web") {
          scrollTo("bank");
        }
      }
      setCardLoading(false);
      setBankLoading(false);
    }
  }, [paymentSources]);
  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={155}>
      <SafeAreaView style={styles.container}>
        <ScrollView
          ref={scrollViewRef}
          style={styles.scrollView}
          keyboardShouldPersistTaps={"never"}>
          <View style={{ marginTop: 24 }} />
          <Grid>
            <Container style={{ width: "100%", paddingHorizontal: 0 }}>
              {paymentSources?.length >= 0 || paymentSourcesError ? (
                <View>
                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                      <View style={styles.accordionContainer}>
                        <SimpleAccordion
                          accordionStyle={styles.accordionStyle}
                          title={
                            <TextComponent
                              style={[typography.title2, styles.accordionTitle]}
                              {...automationTestingLocators.walletPaymentMethods.savedCardsTitle}>
                              {translate("walletPaymentMethod.card.title")}
                            </TextComponent>
                          }
                          expanded={expandCreditCard}
                          rightIconColor={componentTheme?.colors.primary}
                          showLeftIcon={false}
                          showRightIcon={true}
                          onPress={() => {
                            setExpandCreditCard(!expandCreditCard);
                          }}
                          {...automationTestingLocators.walletPaymentMethods.creditCardAccordion}>
                          <View style={{ padding: 10 }}>
                            {cardLoading ? (
                              <ActivityIndicator />
                            ) : creditCardPaymentMethod(paymentSources)?.length > 0 ? (
                              creditCardPaymentMethod(paymentSources).map((card: any) => {
                                return (
                                  <>
                                    <View style={{ marginTop: 15 }} />
                                    <PaymentMethod
                                      key={card.id}
                                      useRadioButton={false}
                                      paymentType={card.type}
                                      active={card?.active}
                                      last_four_digits={card?.last_four_digits}
                                      bank_account_type={card?.bank_account_type}
                                      allowedPayments={card?.allowedPayments}
                                      withinCertifiedDateRange={card?.withinCertifiedDateRange}
                                      checkedStatus={card?.checkedStatus}
                                      setSelectedWrapped={card?.setSelectedWrapped}
                                      fullName={card?.fullName}
                                      card_type={card?.card_type}
                                      shouldDisableCC={card?.shouldDisableCC}
                                      updateNickname={updatePaymentMethodNickname}
                                      deleteOption={true}
                                      updated={() => {
                                        setUpdated(!updated);
                                      }}
                                      {...card}
                                    />
                                  </>
                                );
                              })
                            ) : (
                              <TextComponent
                                style={[
                                  typography.subHeading,
                                  {
                                    fontWeight: fontWeights.Regular,
                                    color: componentTheme.colors.grey,
                                  },
                                ]}
                                {...automationTestingLocators.walletPaymentMethods.noSavedCardText}>
                                {translate("walletPaymentMethod.card.noSavedCards")}
                              </TextComponent>
                            )}

                            <View style={[{ alignItems: "center", marginTop: 15 }]}>
                              <CommonButton
                                text={translate("walletPaymentMethod.card.addButton")}
                                onPress={() => {
                                  refCCDebit?.current?.open();
                                  setToggleForCardBottomSheet(!toggleForCardBottomSheet);
                                }}
                                testProp={
                                  automationTestingLocators.walletPaymentMethods.addCreditCardButton
                                }
                              />
                            </View>
                          </View>
                        </SimpleAccordion>
                      </View>
                    </Column>
                  </Row>
                  <View ref={bankViewRef} style={{ marginTop: 24 }} onLayout={onLayout} />

                  <Row>
                    <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                    <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                      <View style={styles.accordionContainer}>
                        <SimpleAccordion
                          accordionStyle={styles.accordionStyle}
                          title={
                            <TextComponent
                              style={[typography.title2, styles.accordionTitle]}
                              {...automationTestingLocators.walletPaymentMethods.savedBankTitle}>
                              {translate("walletPaymentMethod.bank.title")}
                            </TextComponent>
                          }
                          expanded={expandBankAccount}
                          rightIconColor={componentTheme?.colors.primary}
                          showLeftIcon={false}
                          showRightIcon={true}
                          onPress={() => {
                            setExpandBankAccount(!expandBankAccount);
                          }}
                          {...automationTestingLocators.walletPaymentMethods.bankAccountAccordion}>
                          <View style={{ padding: 10 }}>
                            {bankLoading ? (
                              <ActivityIndicator />
                            ) : bankAccountPaymentMethod(paymentSources)?.length > 0 ? (
                              bankAccountPaymentMethod(paymentSources).map((item: any) => {
                                return (
                                  <>
                                    <View style={{ marginTop: 15 }} />
                                    <PaymentMethod
                                      key={item.id}
                                      useRadioButton={false}
                                      paymentType={item.type}
                                      active={item?.active}
                                      last_four_digits={item?.last_four_digits}
                                      bank_account_type={item?.bank_account_type}
                                      allowedPayments={item?.allowedPayments}
                                      withinCertifiedDateRange={item?.withinCertifiedDateRange}
                                      checkedStatus={item?.checkedStatus}
                                      setSelectedWrapped={item?.setSelectedWrapped}
                                      fullName={item?.fullName}
                                      card_type={item?.card_type}
                                      shouldDisableCC={item?.shouldDisableCC}
                                      updateNickname={updatePaymentMethodNickname}
                                      deleteOption={true}
                                      updated={() => {
                                        setUpdated(!updated);
                                      }}
                                      {...item}
                                    />
                                  </>
                                );
                              })
                            ) : (
                              <TextComponent
                                style={[
                                  typography.subHeading,
                                  {
                                    fontWeight: fontWeights.Regular,
                                    color: componentTheme.colors.grey,
                                  },
                                ]}
                                {...automationTestingLocators.walletPaymentMethods.noSavedBankText}>
                                {translate("walletPaymentMethod.bank.noSavedBanks")}
                              </TextComponent>
                            )}

                            <View style={[{ alignItems: "center", marginTop: 15 }]}>
                              <CommonButton
                                text={translate("walletPaymentMethod.bank.addButton")}
                                onPress={() => {
                                  refBankAccount?.current?.open();
                                  setToggleForBankBottomSheet(!toggleForBankBottomSheet);
                                }}
                                testProp={
                                  automationTestingLocators.walletPaymentMethods
                                    .addBankAccountButton
                                }
                              />
                            </View>
                          </View>
                        </SimpleAccordion>
                      </View>
                    </Column>
                  </Row>
                </View>
              ) : (
                <FullScreenLoader loading={true} />
              )}

              <View style={{ marginTop: 24 }} />
            </Container>
          </Grid>
          <AddPaymentCCDebit
            ref={refCCDebit}
            reservationId={firstReservationId}
            updated={() => {
              setCardLoading(true);
              setUpdated(!updated);
              scrollTo("card");
            }}
            {...route.params}
            isDomusoCash={true}
            toggleForCardBottomSheet={toggleForCardBottomSheet}
            showStoreCardCheckbox={false}
          />
          <AddPaymentBA
            updated={() => {
              setBankLoading(true);
              setUpdated(!updated);
            }}
            ref={refBankAccount}
            reservationId={firstReservationId}
            toggleForBankBottomSheet={toggleForBankBottomSheet}
            showStoreBankCheckbox={false}
            {...route.params}
          />

          <ErrorWarningBottomSheet
            ref={refPopupWarning}
            title={translate("walletPaymentMethod.error") as string}
            body={errorMsg}
            height={170}
            alertType={"error"}
            showButtons={false}
            {...automationTestingLocators.walletPaymentMethods.errorWarningAlert}
          />
        </ScrollView>
        <Footer />
      </SafeAreaView>
    </KeyboardAvoidingView>
  );
};

const getStyles = () => {
  return StyleSheet.create({
    accordionTitle: {
      fontFamily: "Lato_700Bold",
      fontWeight: fontWeights.Bold,
      color: componentTheme.colors.darkerGrey,
      fontSize: componentTheme.fontSize.title1,
    },
    accordionContainer: { backgroundColor: componentTheme.colors.white, padding: 0 },
    accordionContainerWeb: {
      backgroundColor: componentTheme.colors.white,
      padding: 15,
      marginHorizontal: 25,
      borderRadius: 10,
      paddingHorizontal: 20,
    },
    accordionStyle: {
      paddingLeft: 15,
      paddingBottom: 15,
      paddingTop: 15,
      paddingRight: 15,
    },
    container: {
      backgroundColor: "#F3F3F3",
      padding: 0,
      flex: 1,
    },
    scrollView: {
      flexGrow: 0,
    },
  });
};

export default WalletPaymentMethods;
